// Minimal, dependency-free ZIP writer using the STORE method (no
// compression). Photos and videos are already compressed, so deflating them
// would burn CPU for ~0% size gain — STORE is the correct choice and keeps
// this to ~80 lines with no npm dependency.
//
// Limitations: builds the whole archive in memory (peak ≈ sum of entry
// sizes) and uses 32-bit ZIP fields (no ZIP64), so it's meant for normal
// multi-select batches, not multi-GB archives. The caller (batch download)
// notes this to the user.

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(buf: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < buf.length; i++) {
    c = CRC_TABLE[(c ^ buf[i]!)! & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/// True if these entries would overflow ZIP's 32-bit size/offset fields.
/// (~4 GiB total.) Callers should fall back to per-file downloads.
export function exceedsZip32(entries: { size: number }[]): boolean {
  const total = entries.reduce((s, e) => s + e.size, 0);
  return total >= 0xffffffff;
}

/// Build a STORE-method .zip Blob from the given entries.
export function zipStore(entries: ZipEntry[]): Blob {
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const e of entries) {
    const nameBytes = enc.encode(e.name);
    const crc = crc32(e.data);
    const size = e.data.length;

    const lh = new Uint8Array(30 + nameBytes.length);
    const ldv = new DataView(lh.buffer);
    ldv.setUint32(0, 0x04034b50, true); // local file header sig
    ldv.setUint16(4, 20, true); // version needed
    ldv.setUint16(6, 0x0800, true); // flags: UTF-8 filename
    ldv.setUint16(8, 0, true); // method: store
    ldv.setUint16(10, 0, true); // mod time
    ldv.setUint16(12, 0, true); // mod date
    ldv.setUint32(14, crc, true);
    ldv.setUint32(18, size, true); // compressed
    ldv.setUint32(22, size, true); // uncompressed
    ldv.setUint16(26, nameBytes.length, true);
    ldv.setUint16(28, 0, true); // extra len
    lh.set(nameBytes, 30);
    parts.push(lh, e.data);

    const ch = new Uint8Array(46 + nameBytes.length);
    const cdv = new DataView(ch.buffer);
    cdv.setUint32(0, 0x02014b50, true); // central dir header sig
    cdv.setUint16(4, 20, true); // version made by
    cdv.setUint16(6, 20, true); // version needed
    cdv.setUint16(8, 0x0800, true); // flags
    cdv.setUint16(10, 0, true); // method
    cdv.setUint16(12, 0, true); // time
    cdv.setUint16(14, 0, true); // date
    cdv.setUint32(16, crc, true);
    cdv.setUint32(20, size, true);
    cdv.setUint32(24, size, true);
    cdv.setUint16(28, nameBytes.length, true);
    cdv.setUint16(30, 0, true); // extra
    cdv.setUint16(32, 0, true); // comment
    cdv.setUint16(34, 0, true); // disk start
    cdv.setUint16(36, 0, true); // internal attrs
    cdv.setUint32(38, 0, true); // external attrs
    cdv.setUint32(42, offset, true); // local header offset
    ch.set(nameBytes, 46);
    central.push(ch);

    offset += lh.length + size;
  }

  const cdSize = central.reduce((s, c) => s + c.length, 0);
  const cdOffset = offset;

  const eocd = new Uint8Array(22);
  const edv = new DataView(eocd.buffer);
  edv.setUint32(0, 0x06054b50, true); // end of central dir sig
  edv.setUint16(4, 0, true); // disk number
  edv.setUint16(6, 0, true); // cd start disk
  edv.setUint16(8, entries.length, true); // entries this disk
  edv.setUint16(10, entries.length, true); // total entries
  edv.setUint32(12, cdSize, true);
  edv.setUint32(16, cdOffset, true);
  edv.setUint16(20, 0, true); // comment len

  return new Blob([...parts, ...central, eocd] as unknown as BlobPart[], {
    type: 'application/zip',
  });
}
